import React, { useMemo } from 'react'
import { connect } from 'react-redux'
import { SubmissionError } from 'redux-form'

import QS from 'qs'
import { useTranslation } from 'react-i18next'
import { toast, cssTransition } from 'react-toastify'

import {
  setCurrentUserToken,
  setCurrentUserAuthProvider,
} from 'ducks/users/index.ts'
import { getAttributionCookies } from 'utils/attributionCookies'
import { authStart, authFinalize } from 'utils/io'

import LoginForm from './AuthV2/LoginForm.tsx'
import AuthPage from './AuthV2/AuthPage.tsx'

import './Auth.scss'

const ERROR_MESSAGES = {
  AUTH_FAILED: 'User not found. Sign up first',
  NOT_FOUND: 'User not found. Sign up first',
  USER_NOT_FOUND: 'User not found. Sign up first',
  TWO_FACTOR_NOT_VALIDATED: 'Two factor not validated',
  ALREADY_CONSUMED: 'Session expired, try again',
  EXPIRED: 'Session expired, try again',
}

const Login = props => {
  const { t } = useTranslation()
  const { location, history, setCurrentUserToken, setCurrentUserAuthProvider } =
    props

  const redirect = useMemo(() => {
    const { redirect } = QS.parse(location.search, {
      ignoreQueryPrefix: true,
    })

    return redirect
  }, [location])

  const handleLogin = async data => {
    try {
      const response = await authStart({ ...data, provider: 'custom' })

      if (response.requires2fa) {
        localStorage.setItem('twoFactorData', response.attemptToken)
        history.push('/2fa')

        return
      }

      const finalized = await authFinalize({
        attemptToken: response.attemptToken,
      })

      setCurrentUserToken(finalized.sessionToken)
      setCurrentUserAuthProvider(finalized.lastUsedAuthProvider)

      //If they have an emailValidationString but no emailValidatedAt, send them to email validation page
      if (finalized.validateEmail) {
        const emailValidationRedirect =
          redirect && redirect.includes('email-validation')
            ? redirect
            : '/email-validation'
        history.push(emailValidationRedirect)
      } else if (response.stillOnboarding) {
        history.push('/')
      } else {
        history.push(redirect || '/')
      }
    } catch (err) {
      throw new SubmissionError({ _error: t('Invalid email or password') })
    }
  }

  const handleLoginWithGoogle = async token => {
    try {
      if (!token) return alert('No Google credential')

      const response = await authStart({
        token,
        provider: 'google',
        ...getAttributionCookies(),
      })

      if (response.requires2fa) {
        localStorage.setItem('twoFactorData', response.attemptToken)
        history.push('/2fa')

        return
      }

      const finalized = await authFinalize({
        attemptToken: response.attemptToken,
      })

      setCurrentUserToken(finalized.sessionToken)
      setCurrentUserAuthProvider(finalized.lastUsedAuthProvider)

      //If they have an emailValidationString but no emailValidatedAt, send them to email validation page
      if (response.validateEmail) {
        const emailValidationRedirect =
          redirect && redirect.includes('email-validation')
            ? redirect
            : '/email-validation'
        history.push(emailValidationRedirect)
      } else if (response.stillOnboarding) {
        history.push('/')
      } else {
        history.push(redirect || '/')
      }
    } catch (err) {
      toast(
        <div>
          <p>{ERROR_MESSAGES[err.message] ?? 'Unknown error'}.</p>
        </div>,
        {
          position: 'bottom-right',
          className: 'token-expired__toast',
          bodyClassName: 'token-expired__toast-body',
          hideProgressBar: true,
          autoClose: 5000,
          transition: cssTransition({
            enter: 'animate__animated animate__fadeInUp',
            exit: 'animate__animated animate__fadeOutDown',
            collapse: false,
          }),
        }
      )
    }
  }

  return (
    <AuthPage className="login-page" version={2}>
      <LoginForm
        onSubmit={handleLogin}
        handleSubmitWithGoogle={handleLoginWithGoogle}
        redirect={redirect}
      />
    </AuthPage>
  )
}

export default connect(null, {
  setCurrentUserToken,
  setCurrentUserAuthProvider,
})(Login)
